/**
 * Robust clipboard helper.
 *
 * The Clipboard API can be blocked when running inside an iframe (preview
 * environments, certain browser security policies). We try the modern
 * `navigator.clipboard.writeText` first and fall back to the legacy
 * `execCommand("copy")` path when blocked. Both paths return a Promise so
 * callers can show a toast on success and an actionable error on failure.
 *
 * Usage:
 *   import { copyText } from "@/lib/clipboard";
 *   copyText(url).then(() => toast.success("Copied")).catch(() => toast.error("Couldn't copy — long-press to copy manually"));
 */

export async function copyText(text) {
  // Modern path
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      // fall through to legacy path
    }
  }
  // Legacy path: hidden textarea + execCommand. Works in iframes when the
  // user triggered the action (click handler) and Permissions-Policy hasn't
  // blocked clipboard-write entirely. We restore focus + selection so the
  // caller's UI isn't disturbed.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    const prevActive = document.activeElement;
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (prevActive && typeof prevActive.focus === "function") prevActive.focus();
    if (ok) return true;
  } catch (e) {
    // fall through
  }
  throw new Error("Clipboard unavailable in this environment");
}
